import { local, relative, utc, utcTime, NO_VALUE } from './format.ts';
import { useNow } from './useNow.ts';
import { StatusDot } from './StatusDot.tsx';
import type { Tone } from './state.ts';
import styles from './Timestamp.module.css';

/* ==========================================================================
   Timestamp.

   Shows the relative age (the thing an operator scans) and carries the full
   absolute forms — Zulu and local — in its title. The operator must never
   have to ask "is this now?", so a reading past its freshness window changes
   colour rather than sitting there looking current.
   ========================================================================== */

export function Timestamp({
  value,
  mode = 'relative',
  /** Seconds after which the age reads as stale, then as dead. */
  staleAfter,
  deadAfter,
  /**
   * The mirror image, for a deadline rather than an age: seconds of remaining
   * life at which a FUTURE timestamp starts reading as a warning, then as a
   * fault. A certificate that expires in nine days is not stale — it is a job
   * for this week — and the two cases must not share a threshold.
   */
  warnWithin,
  badWithin,
  strong = false,
  live = true,
}: {
  value: string | null | undefined;
  mode?: 'relative' | 'utc' | 'local' | 'utc-time' | undefined;
  staleAfter?: number | undefined;
  deadAfter?: number | undefined;
  warnWithin?: number | undefined;
  badWithin?: number | undefined;
  strong?: boolean | undefined;
  /** false freezes the age — for a panel scrubbed to history. */
  live?: boolean | undefined;
}) {
  const now = useNow(live && mode === 'relative');

  if (!value) {
    return <span className={styles.ts} data-strong={strong}>{NO_VALUE}</span>;
  }

  const t = new Date(value).getTime();
  const ageS = Number.isNaN(t) ? null : (now - t) / 1000;
  /* Remaining life, for a deadline. Already elapsed counts as zero remaining,
     so an expired certificate reads as the fault it is rather than wrapping
     back into "plenty of time". */
  const leftS = ageS === null ? null : -ageS;
  const stale =
    (staleAfter !== undefined && ageS !== null && ageS > staleAfter) ||
    (warnWithin !== undefined && leftS !== null && leftS <= warnWithin);
  const dead =
    (deadAfter !== undefined && ageS !== null && ageS > deadAfter) ||
    (badWithin !== undefined && leftS !== null && leftS <= badWithin);

  const text =
    mode === 'relative' ? relative(value, now)
      : mode === 'utc' ? utc(value)
        : mode === 'utc-time' ? utcTime(value)
          : local(value);

  return (
    <time
      className={styles.ts}
      dateTime={value}
      data-strong={strong}
      data-stale={stale && !dead}
      data-dead={dead}
      data-expiry={badWithin !== undefined || warnWithin !== undefined}
      title={`${utc(value)}  ·  ${local(value)}  ·  ${relative(value, now)}`}
    >
      {text}
    </time>
  );
}

/**
 * The header clock. Zulu, monospace, with the connection word beside it, so
 * "is this now, and is this real?" is answered without moving the mouse.
 */
export function UtcClock({
  connected,
  label,
  tone,
}: {
  connected: boolean;
  /** The word beside the clock. Defaults to Online / Offline. */
  label?: string | undefined;
  /**
   * Override the tone when "not pushing" is not the same as "not working" —
   * a console falling back to REST polling is `nominal`, not `offline`.
   */
  tone?: Tone | undefined;
}) {
  const now = useNow(true);
  const iso = new Date(now).toISOString();
  const t: Tone = tone ?? (connected ? 'live' : 'offline');
  return (
    <span className={styles.clock} data-tone={t}>
      <StatusDot tone={t} hollow={!connected} pulse={connected} />
      <span className={styles.clockWord}>{label ?? (connected ? 'Online' : 'Offline')}</span>
      <time dateTime={iso} title={`${utc(iso)}  ·  ${local(iso)}`}>{utc(iso)}</time>
    </span>
  );
}
